import { useState, useCallback, useEffect } from "react";
import {
  type Player,
  type Board as BoardType,
  type Scores,
  INITIAL_BOARD,
  checkWinner,
  checkDraw,
  loadScores,
  saveScores,
  clearScores,
} from "./api/gameData";
import { Board } from "./components/Board";
import { ScoreBoard } from "./components/ScoreBoard";
import { TurnIndicator } from "./components/TurnIndicator";
import { GameControls } from "./components/GameControls";
import "./index.css";

export function App() {
  const [board, setBoard] = useState<BoardType>([...INITIAL_BOARD]);
  const [currentPlayer, setCurrentPlayer] = useState<Player>("X");
  const [scores, setScores] = useState<Scores>(loadScores);
  const [winningLine, setWinningLine] = useState<number[] | null>(null);
  const [winner, setWinner] = useState<Player | null>(null);
  const [isDraw, setIsDraw] = useState(false);

  const gameOver = winner !== null || isDraw;

  // Persist scores whenever they change
  useEffect(() => {
    saveScores(scores);
  }, [scores]);

  const handleCellClick = useCallback(
    (index: number) => {
      if (board[index] || gameOver) return;

      const newBoard = [...board];
      newBoard[index] = currentPlayer;
      setBoard(newBoard);

      const result = checkWinner(newBoard);
      if (result) {
        setWinner(result.winner);
        setWinningLine(result.line);
        setScores((prev) => ({
          ...prev,
          [result.winner]: prev[result.winner] + 1,
        }));
        return;
      }

      if (checkDraw(newBoard)) {
        setIsDraw(true);
        setScores((prev) => ({ ...prev, draws: prev.draws + 1 }));
        return;
      }

      setCurrentPlayer(currentPlayer === "X" ? "O" : "X");
    },
    [board, currentPlayer, gameOver]
  );

  const handlePlayAgain = useCallback(() => {
    setBoard([...INITIAL_BOARD]);
    setWinningLine(null);
    setWinner(null);
    setIsDraw(false);
    // Alternate who goes first — loser/drawer starts, or just swap
    setCurrentPlayer((prev) => (prev === "X" ? "O" : "X"));
  }, []);

  const handleReset = useCallback(() => {
    setBoard([...INITIAL_BOARD]);
    setWinningLine(null);
    setWinner(null);
    setIsDraw(false);
    setCurrentPlayer("X");
    setScores({ X: 0, O: 0, draws: 0 });
    clearScores();
  }, []);

  return (
    <div className="relative z-10 w-full min-h-screen flex flex-col items-center justify-center px-4 py-8 gap-8 sm:gap-10">
      {/* Title */}
      <h1
        className="text-3xl sm:text-4xl font-extrabold tracking-tight"
        style={{
          fontFamily: "var(--font-display)",
          color: "var(--game-text)",
          animation: "fadeIn 0.5s ease-out",
        }}
      >
        <span style={{ color: "var(--color-x)" }}>Tic</span>
        <span style={{ color: "var(--game-text-faint)", margin: "0 0.25em" }}>·</span>
        <span style={{ color: "var(--game-text)" }}>Tac</span>
        <span style={{ color: "var(--game-text-faint)", margin: "0 0.25em" }}>·</span>
        <span style={{ color: "var(--color-o)" }}>Toe</span>
      </h1>

      {/* Scoreboard */}
      <ScoreBoard scores={scores} />

      {/* Turn / Status */}
      <TurnIndicator
        currentPlayer={currentPlayer}
        winner={winner}
        isDraw={isDraw}
      />

      {/* Board */}
      <Board
        board={board}
        winningLine={winningLine}
        onCellClick={handleCellClick}
        disabled={gameOver}
      />

      {/* Controls */}
      <GameControls
        gameOver={gameOver}
        onPlayAgain={handlePlayAgain}
        onReset={handleReset}
      />
    </div>
  );
}

export default App;
